<template>
  <div class="loans-container">

    <base-navigation></base-navigation>

    <!-- Header Card -->
    <div class="top-card">
      <div class="title-section">
        <h2>Loans</h2>
        <span class="count">{{ filteredLoans.length }} Active</span>
      </div>

      <button @click="$router.push('/new/loan')" class="new-loan-btn">
        + New Loan
      </button>
    </div>

    <!-- Search -->
    <div class="search-box">
      <input
        v-model="search"
        type="text"
        placeholder="Search name, phone, ID, or bank..."
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state loading">
      Loading loans...
    </div>

    <!-- Error -->
    <div v-if="error" class="state error">
      {{ error }}
    </div>

    <!-- Table -->
    <div v-if="!loading && filteredLoans.length > 0" class="card">
      <div class="table-wrapper">
        <table class="loans-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Client</th>
              <th>ID No</th>
              <th>Phone</th>
              <th>Bank</th>
              <th>Loan</th>
              <th>Interest</th>
              <th>Balance</th>
              <th>Last Payment Date</th>
              <th>Last Payment Amt</th>
              <th>Interest Paid</th>
              <th>Total Repayment</th>
              <th>Issued</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="loan in filteredLoans" :key="loan.id">
              <td class="id">#{{ loan.id }}</td>

              <td>
                <div class="client-name">
                  {{ loan.client_name }}
                </div>
              </td>

              <td>{{ loan.idNumber }}</td>
              <td>{{ loan.phone || '—' }}</td>
              <td>{{ loan.bank || '—' }}</td>

              <td>{{ formatCurrency(loan.loan_amount) }}</td>

              <td class="interest">
                {{ formatCurrency(loan.interest_balance) }}
              </td>

              <td>
                <span
                  class="balance-badge"
                  :class="loan.loan_balance > 0 ? 'active' : 'cleared'"
                >
                  {{ formatCurrency(loan.loan_balance) }}
                </span>
              </td>

              <td>{{ loan.last_payment_date }}</td>

              <td>
                <span v-if="loan.last_payment_amount">
                  {{ formatCurrency(loan.last_payment_amount) }}
                </span>
                <span v-else class="muted">—</span>
              </td>

              <td class="id">{{ loan.interest_paid }}</td>
              <td class="id">{{ loan.total_paid }}</td>

              <td>{{ formatDate(loan.createdAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- No Data -->
    <div v-if="!loading && filteredLoans.length === 0" class="state">
      No matching loans found
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "LoansList",

  data() {
    return {
      loans: [],
      loading: false,
      error: null,
      search: ""
    };
  },

  mounted() {
    this.fetchLoans();
  },

  computed: {
    filteredLoans() {
      if (!this.search) return this.loans;

      const term = this.search.toLowerCase();

      return this.loans.filter(loan => {
        return (
          (loan.client_name &&
            loan.client_name.toLowerCase().includes(term)) ||
          (loan.phone &&
            loan.phone.toLowerCase().includes(term)) ||
          (loan.idNumber &&
            loan.idNumber.toString().includes(term)) ||
          (loan.bank &&
            loan.bank.toLowerCase().includes(term))
        );
      });
    }
  },

  methods: {
    async fetchLoans() {
      this.loading = true;

      try {
        const [loansRes, clientsRes] = await Promise.all([
          axios.get("https://api.roberms.com/raisetrack/get/loans"),
          axios.get("https://api.roberms.com/raisetrack/get/loan/clients")
        ]);

        const loans = loansRes.data;
        const clients = clientsRes.data;

        // Map clients by idNumber
        const clientMap = {};
        clients.forEach(client => {
          clientMap[client.idNumber] = client;
        });

        // Merge
        this.loans = loans.map(loan => {
          const client = clientMap[loan.idNumber];

          return {
            ...loan,
            phone: client ? client.phone_number : null,
            bank: client ? client.bank : null
          };
        });

      } catch (err) {
        this.error = "Failed to load loans";
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES"
      }).format(value);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.loans-container {
  padding: 20px;
  background: #f4f6f9;
  min-height: 100vh;
}

/* Header */
.top-card {
  background: white;
  padding: 15px 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.count {
  background: #e3f2fd;
  color: #1976d2;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
}

/* Search */
.search-box {
  margin-bottom: 10px;
}

.search-box input {
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 13px;
}

.search-box input:focus {
  outline: none;
  border-color: #1976d2;
}

/* Button */
.new-loan-btn {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
}

/* Table */
.card {
  background: white;
  border-radius: 10px;
  padding: 10px;
}

.table-wrapper {
  overflow-x: auto;
}

.loans-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.loans-table th {
  text-align: left;
  padding: 10px;
  background: #fafafa;
}

.loans-table td {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

/* Styling */
.id {
  font-weight: bold;
  color: #1976d2;
}

.client-name {
  font-weight: 600;
}

.interest {
  color: #ff9800;
  font-weight: 600;
}

.balance-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: bold;
}

.balance-badge.active {
  background: #ffebee;
  color: #d32f2f;
}

.balance-badge.cleared {
  background: #e8f5e9;
  color: #2e7d32;
}

.state {
  padding: 20px;
  text-align: center;
}

.loading {
  color: #1976d2;
}

.error {
  color: red;
}

.muted {
  color: #999;
}
</style>